<template>
    <div class="search-bar-wrapper d-flex align-center">

      <!-- SEARCH INPUT -->
      <div class="sas-search-bar d-flex align-center" :class="{ 'sas-focused': isFocused }">
        <v-icon>mdi-magnify</v-icon>

        <v-text-field
            ref="searchInput"
            v-model="searchData.query"
            class=""
            dense
            hide-details
            solo
            flat
            :readonly="false"
            @click.native="onClickSearchInput"
            :placeholder="searchPlaceholder"
            @focus="onSearchFocusOn"
            @blur="isFocused = false"
            @input="onSearchInput"
            @keydown="onQueryKeydown"
        />

        <div v-if="searchData.loading" class="sas-end-slot d-flex align-center justify-center">
          <v-progress-circular indeterminate size="18" width="2" />
        </div>

        <div v-if="searchData.query" class="sas-end-slot d-flex align-center justify-center cursor-pointer" @click.stop="clearInput">
          <v-icon>mdi-close</v-icon>
        </div>
      </div>

      <!-- SEARCH ByButtons -->
      <div class="sas-actions d-flex align-center">
        <v-btn
            icon
            class="sas-action-btn"
            :class="{ 'is-active': effectiveScope === 'agency' }"
            @click.stop="onScopeClick('agency')"
        >
          <v-icon>mdi-home-outline</v-icon>
        </v-btn>

        <v-btn
            icon
            class="sas-action-btn"
            :class="{ 'is-active': effectiveScope === 'agent' }"
            @click.stop="onScopeClick('agent')"
        >
          <v-icon>mdi-face-agent</v-icon>
        </v-btn>

        <div
             class="cursor-pointer"
             @click="$emit('quick-agent')"
        >
          <v-chip class="cursor-pointer">
            <v-icon small color="grey darken-1">mdi-plus</v-icon>
            <span class="font-weight-medium grey--text text--darken-2">Création rapide</span>
          </v-chip>
        </div>

      </div>
    </div>
</template>

<script>
import debounce from "lodash.debounce";
import { get } from "@/utils/api";
import FormMixin from "@/components/mixins/FormMixin";

export default {
  name: "SmartAgentSearchBar",
  mixins: [FormMixin],
  components: { },
  props: {
    activity: { type: Boolean, default: false },
  },
  emits: ['update:state', 'search-results', 'search-click', 'quick-agent'],
  data: () => ({
    isFocused: false,
    requestKey: 0,
    debouncedFetch: null,
    searchData: {
      query: "",
      scope: "",
      loading: false,
      error: null,
    },
  }),
  created() {
    this.debouncedFetch = debounce(() => {
      this.fetchResults();
    }, 500);

    this._onKeyDown = (e) => {
      const tag = e.target.tagName.toLowerCase()
      const isTypingField =
          tag === "input" ||
          tag === "textarea" ||
          e.target.isContentEditable
      if (isTypingField || this.isSearchActive) return

      if (e.key === '+' || e.key.toLowerCase() === 'n') {
        e.preventDefault()
        e.stopPropagation()
        this.onNewKey()
      }

      if (e.key === '#') {
        e.preventDefault()
        e.stopPropagation()
        this.searchData.scope = this.searchData.scope === "agency" ? "" : "agency"
      }

      if (e.key === '@') {
        e.preventDefault()
        e.stopPropagation()
        this.searchData.scope = this.searchData.scope === "agent" ? "" : "agent"
      }
    }

    window.addEventListener('keydown', this._onKeyDown)
  },
  beforeDestroy() {
    if (this.debouncedFetch && this.debouncedFetch.cancel) this.debouncedFetch.cancel();
    window.removeEventListener('keydown', this._onKeyDown)
  },
  watch: {
    emittedState: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit('update:state', value)
      }
    }
  },
  computed: {
    searchUiState() {
      if (this.searchData.loading) return "loading"
      if (this.searchData.error) return "error"
      if (!this.searchData.query.trim()) return "idle"
      if (!this.canRunSearch) return "typing"
      return "ready"
    },
    emittedState() {
      return {
        rawQuery: this.searchData.query,
        query: this.effectiveQuery,
        scope: this.effectiveScope,
        canSearch: this.canRunSearch,
        isBusy: this.searchData.loading,
        isActive: this.isSearchActive,
        error: this.searchData.error,
        uiState: this.searchUiState,
      }
    },
    canRunSearch() {
      const parts = this.effectiveQuery
          .split(/\s+/)
          .map(t => t.trim())
          .filter(t => t.length > 0)

      const isValidToken = (t) => {
        return (t.length >= 3 || (t.length >= 2 && /^[0-9]+$/.test(t)))
      }
      return parts.some(isValidToken)
    },
    isSearchActive() {
      return (
          this.isFocused ||
          !!this.searchData.query.trim() ||
          this.searchData.loading
      )
    },
    searchPlaceholder() {
      switch (this.searchData.scope) {
        case "agent":
          return "Rechercher un agent..."
        case "agency":
          return "Rechercher une agence..."
        default:
          return "Rechercher un agent, une agence..."
      }
    },
    effectiveScope() {
      const raw = (this.searchData.query || "").trim();

      if (raw.startsWith("@")) return "agent";
      if (raw.startsWith("#")) return "agency";

      return this.searchData.scope || "";
    },
    effectiveQuery() {
      const raw = (this.searchData.query || "").trim();
      if (raw.startsWith("@") || raw.startsWith("#")) {
        return raw.slice(1).trim();
      }
      return raw;
    },
  },
  methods: {
    onClickSearchInput() {
      this.$emit('search-click')
    },
    onSearchFocusOn() {
      this.isFocused = true
    },
    onSearchInput() {
      if (!this.canRunSearch) {
        // this.searchData.results = this.emptyResult();
        this.$emit('search-results', this.emptyResult());
        this.searchData.error = null;
        if (this.debouncedFetch.cancel) this.debouncedFetch.cancel();
        return;
      }
      this.debouncedFetch();
    },
    onScopeClick(scope) {
      this.searchData.scope = (this.searchData.scope === scope) ? "" : scope;
      if (this.searchData.query.startsWith('#') || this.searchData.query.startsWith('@')) {
        this.searchData.query = this.searchData.query.slice(1)
      }

      if (this.canRunSearch) {
        if (this.debouncedFetch && this.debouncedFetch.cancel) this.debouncedFetch.cancel();
        this.fetchResults();
      }
    },
    onNewKey() {
      this.$emit('quick-agent')
    },
    onQueryKeydown(e) {
      const key = e.key;
      if (key !== "Backspace" && key !== "Delete" && key !== "Escape") return;
      const raw = (this.searchData.query || "").trim();
      const isEmptyOrOnlyPrefix = raw === "" || raw === "@" || raw === "#";
      if (!isEmptyOrOnlyPrefix && key !== "Escape") return;
      this.clearInput();
      this.searchData.scope = "";
      if (key === "Escape") {this.blurSearchInput()}
    },
    focusSearchInput() {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus()
      }
    },
    blurSearchInput() {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur()
      }
    },
    clearInput() {
      this.requestKey += 1
      this.searchData.query = "";
      this.$emit('search-results', this.emptyResult());
      this.searchData.error = null;
      this.searchData.loading = false;
      if (this.debouncedFetch.cancel) this.debouncedFetch.cancel();
    },
    fetchResults() {
      const query = this.effectiveQuery;
      if (!this.canRunSearch) return;

      const key = ++this.requestKey;
      this.searchData.loading = true;
      this.searchData.error = null;

      const url = `/api/workshop/search?q=${encodeURIComponent(query)}&scope=${encodeURIComponent(this.effectiveScope)}`;

      get(url).json((result) => {
        if (key !== this.requestKey) return;
        const payload = {
          agents: Array.isArray(result.agents) ? result.agents : [],
          agencies: Array.isArray(result.agencies) ? result.agencies : [],
        }
        this.searchData.loading = false;
        this.$emit('search-results', payload);
      }).catch(() => {
        if (key !== this.requestKey) return;
        this.searchData.error = true;
        this.searchData.loading = false;
        this.$emit('search-results', this.emptyResult());
      })
    },
    emptyResult() {
      return {
        agents: [],
        agencies: [],
      }
    },
  }
}

</script>


<style scoped>

.cursor-pointer {
  cursor: pointer;
}

.search-bar-wrapper {
  flex: 0 0 auto;
  gap: 12px;
  min-width: 0;
  max-width: 800px;
}

.sas-search-bar {
  flex: 1;
  gap: 12px;
  height: 52px;
  padding: 0 16px;
  border: 1px solid #dadce0;
  border-radius: 26px;
  background: white;
  transition: all 0.2s ease;
}

.sas-search-bar.sas-focused {
  border-color: #4099ff;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}

.sas-end-slot {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}

.sas-actions {
  gap: 12px;
}

.sas-action-btn {
  width: 52px;
  height: 52px;
  min-width: 52px !important;
  border-radius: 999px;
  border: 1px solid #dadce0;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.sas-action-btn.is-active {
  color: #4099ff;
  border-color: #4099ff !important;
  background: #f6f8fa;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}

.sas-action-btn:hover {
  color: #4099ff;
  background: #f6f8fa;
  border-color: #4099ff !important;
}

.sas-action-btn >>> .v-icon {
  color: inherit !important;
}

</style>